import type {
  Configuration,
  ListCredentialsProfilesFilterConditions,
  ListIngestionPipelinesFilterConditions,
  ListJobTemplatesFilterConditions,
  ListMaintenanceRunsFilterConditions,
  ListNotebooksFilterConditions,
  ListSavedQueriesFilterConditions,
  ListSparkEnginesFilterConditions,
  ListSparkJobsFilterConditions,
  ListSqlEnginesFilterConditions,
  ListSqlQueryExecutionsFilterConditions,
  SortCredentialsProfileBy,
  SortIngestionPipelineBy,
  SortJobTemplateBy,
  SortMaintenanceRunBy,
  SortNotebookBy,
  SortSavedQueryBy,
  SortSparkEngineBy,
  SortSparkJobBy,
  SortSqlEngineBy,
  SortSqlQueryExecutionBy,
  CatalogAccessRequest,
  CatalogCheckNameRequest,
  CreateCredentialsProfileInput,
  UpdateCredentialsProfileInput,
  CreateDashboardInput,
  CreateDataCatalogInput,
  CreateGraphInput,
  CreateIngestionPipelineInput,
  CreateJobTemplateInput,
  CreateNotebookInput,
  CreateAwsPrivateLinkInput,
  UpdateAwsPrivateLinkInput,
  CreateSavedQueryInput,
  CreateSparkEngineInput,
  CreateSqlEngineInput,
  CreateAwsVpcPeeringInput,
  CreateZeroEtlPolicyInput,
  BucketFileProbeRequest,
  BucketNotificationProbeRequest,
  CatalogListDatabasesRequest,
  KafkaConnectProbeRequest,
  KafkaInferSchemaRequest,
  KafkaSampleProbeRequest,
  KafkaSecretProbeRequest,
  KafkaTopicsProbeRequest,
  GcsListPrefixesRequest,
  S3DetectPartitionsRequest,
  S3ListPrefixesRequest,
  SchemaRegistryConnectRequest,
  SchemaRegistryResolveSubjectRequest,
  SchemaRegistrySchemaByIdRequest,
  UpdateWorkspaceStorageAccessConfigurationInput,
  UpdateWorkspaceLoggingSettingsInput,
  UpdateWorkspaceMaintenanceSettingsRequest,
  UpdateWorkspaceMetricsSettingsInput,
  ExecuteNotebookCodeInput,
  StartNotebookSessionInput,
  StartSparkJobInput,
  StartSqlQueryExecutionInput,
  UpdateDashboardInput,
  UpdateGraphInput,
  UpdateIngestionPipelineInput,
  UpdateJobTemplateInput,
  UpdateSavedQueryInput,
  UpdateTableMaintenanceConfigInput,
  UpdateZeroEtlPolicyInput,
} from '@repo/lakehouse-sdk';
import { LakehouseApi } from '@repo/lakehouse-sdk';
import { mutate } from 'swr';

import type { HttpClient } from './httpClient';

const baseUrl = '/lakehouse';

// Wire types for ``GET /ingestion-pipeline/{id}/metrics`` and
// ``/metrics/range``. Hand-rolled until the SDK regenerates the matching
// classes from the lakehouse OpenAPI; see the matching Pydantic Out DTOs
// in src/lakehouse/interfaces/http/dto/ingestion_pipeline_metrics*.py.
interface IngestionPipelineMetricsPayload {
  observedAt: string;
  sourceRecordsPerSec?: number;
  sinkRecordsPerSec?: number;
  sourceBytesPerSec?: number;
  sourceTotalRecordsRead?: number;
  sinkTotalRecordsWritten?: number;
  sourceBacklogRecords?: number;
  sourceFilesPending?: number;
  sourceOldestPendingAgeSeconds?: number;
  dlqMessages?: number;
  invalidRecordsPerSec?: number;
  errorRate1hPct?: number;
  secondsSinceLastCommit?: number;
  completedCheckpointsTotal?: number;
  lagSeconds?: number;
  jvmHeapUsedBytesMax?: number;
  jvmHeapMaxBytesMax?: number;
  lastCheckpointDurationMs?: number;
  lastCheckpointSizeBytes?: number;
  failedCheckpointsTotal?: number;
  restartsTotal?: number;
  backpressureMaxRatio?: number;
}

interface MetricTimeseriesPoint {
  ts: number;
  value?: number;
}

interface MetricTimeseries {
  series: string;
  startTs: number;
  endTs: number;
  stepSeconds: number;
  points: MetricTimeseriesPoint[];
}

interface IngestionPipelineMetricsRangePayload {
  startTs: number;
  endTs: number;
  stepSeconds: number;
  seriesByName: Record<string, MetricTimeseries>;
}

/**
 * Wraps an async function to handle 204 No Content responses gracefully.
 * The generated SDK tries to parse empty 204 responses as JSON, throwing a SyntaxError.
 * This wrapper catches that specific error and treats it as success.
 */
const handle204 = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
  try {
    return await fn();
  } catch (error) {
    // 204 No Content causes JSON parsing to fail - treat as success
    if (
      error instanceof SyntaxError ||
      (error instanceof Error &&
        error.message.includes('Unexpected end of JSON input'))
    ) {
      return undefined;
    }
    throw error;
  }
};

/**
 * Iterates pages of a lakehouse list endpoint up to `maxResources` items (or
 * `maxPages` pages, whichever comes first) and returns the distinct values
 * collected by `collect`, sorted. The cap is on sampled rows, NOT distinct
 * count — capping by distinct count would be unbounded when rare values
 * exist. Used to populate filter dropdowns with bounded latency.
 */
async function sampleDistinct<TResp, TItem>(
  fetchPage: (pageToken: string | undefined) => Promise<TResp>,
  getItems: (resp: TResp) => TItem[] | undefined,
  getNextToken: (resp: TResp) => string | null | undefined,
  collect: (item: TItem, set: Set<string>) => void,
  maxResources: number,
  maxPages: number
): Promise<string[]> {
  const set = new Set<string>();
  let pageToken: string | undefined;
  let pageCount = 0;
  let sampled = 0;
  while (pageCount < maxPages && sampled < maxResources) {
    const resp = await fetchPage(pageToken);
    for (const item of getItems(resp) ?? []) {
      sampled++;
      collect(item, set);
      if (sampled >= maxResources) {
        break;
      }
    }
    const next = getNextToken(resp);
    if (!next) {
      break;
    }
    pageToken = next;
    pageCount++;
  }
  return Array.from(set).sort();
}

export const lakehouse = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const lakehouseApi = new LakehouseApi(config);

  /**
   * Build a cursor-token-paginated SWR hook for any lakehouse list endpoint.
   * Captures the common executeWithConfig wiring + `keepPreviousData: true`
   * so all paginated list hooks stay in sync.
   */
  function buildPaginatedListHook<TSort, TFilter, TResp>(
    apiMethod: (
      projectId: string,
      workspaceId: string,
      pageToken?: string,
      pageSize?: number,
      body?: { sorts?: TSort[]; filters?: TFilter }
    ) => Promise<TResp>,
    urlPath: string,
    refreshIntervalMs: number
  ) {
    return function useListPaginated(
      workspaceId: string | null,
      pageToken: string | undefined,
      pageSize: number,
      sorts?: TSort[],
      filters?: TFilter
    ) {
      return httpClient.executeWithConfig(
        (
          projectId: string,
          ws: string,
          pt: string | undefined,
          ps: number,
          s: TSort[] | undefined,
          f: TFilter | undefined
        ) => apiMethod(projectId, ws, pt, ps, { sorts: s, filters: f }),
        baseUrl,
        urlPath,
        { refreshInterval: refreshIntervalMs, keepPreviousData: true },
        currentProjectId,
        workspaceId ?? '',
        pageToken,
        pageSize,
        sorts,
        filters
      );
    };
  }

  return {
    // Workspace operations (project-level, no workspaceId needed)
    listWorkspaces: () => lakehouseApi.listWorkspaces(currentProjectId),
    useGetWorkspace: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.getWorkspace.bind(lakehouseApi),
        baseUrl,
        'getWorkspace',
        currentProjectId,
        workspaceId ?? ''
      ),

    // SQL Engine operations.
    // Non-paginated helper for dropdowns and panels — pageSize=200 (spec max)
    // so consumers get all engines in a single fetch. Use
    // `useListSqlEnginesPaginated` for the engines page itself, which threads
    // the cursor token, sorts, and filters through to the backend.
    useListSqlEngines: (workspaceId: string | null) =>
      httpClient.execute(
        (projectId: string, ws: string) =>
          lakehouseApi.listSqlEngines(projectId, ws, undefined, 200),
        baseUrl,
        'listSqlEngines',
        currentProjectId,
        workspaceId ?? ''
      ),
    useListSqlEnginesPaginated: buildPaginatedListHook<
      SortSqlEngineBy,
      ListSqlEnginesFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listSqlEngines>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listSqlEngines(projectId, ws, pt, ps, body),
      'listSqlEnginesPaginated',
      5000
    ),
    listDistinctNamesFromSqlEngines: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listSqlEngines(currentProjectId, workspaceId, pt, 200),
        (r) => r.sqlEngines,
        (r) => r.nextToken,
        (e, set) => {
          if (e.name) {
            set.add(e.name);
          }
        },
        maxResources,
        maxPages
      ),
    createSqlEngine: (workspaceId: string, request: CreateSqlEngineInput) =>
      lakehouseApi.createSqlEngine(currentProjectId, workspaceId, request),
    deleteSqlEngine: (workspaceId: string, sqlEngineId: string) =>
      handle204(() =>
        lakehouseApi.deleteSqlEngine(currentProjectId, workspaceId, sqlEngineId)
      ),
    useGetSqlEngine: (workspaceId: string | null, sqlEngineId: string | null) =>
      httpClient.execute(
        lakehouseApi.getSqlEngine.bind(lakehouseApi),
        workspaceId && sqlEngineId ? baseUrl : null,
        'getSqlEngine',
        currentProjectId,
        workspaceId ?? '',
        sqlEngineId ?? ''
      ),
    startSqlEngine: (workspaceId: string, sqlEngineId: string) =>
      lakehouseApi.startSqlEngine(currentProjectId, workspaceId, sqlEngineId),
    stopSqlEngine: (workspaceId: string, sqlEngineId: string) =>
      lakehouseApi.stopSqlEngine(currentProjectId, workspaceId, sqlEngineId),

    listDistinctEngineNamesFromExecutions: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listSqlQueryExecutions(
            currentProjectId,
            workspaceId,
            pt,
            200
          ),
        (r) => r.sqlQueryExecutions,
        (r) => r.nextToken,
        (e, set) => {
          if (e.sqlEngineName) {
            set.add(e.sqlEngineName);
          }
        },
        maxResources,
        maxPages
      ),

    // SQL Query Execution operations.
    // The lakehouse backend uses opaque cursor tokens (returned as
    // `nextToken` in the previous response) — there is no random-access
    // pageIndex on the wire. The page-level caller tracks tokens by index
    // and passes the right token here.
    useListSqlQueryExecutions: buildPaginatedListHook<
      SortSqlQueryExecutionBy,
      ListSqlQueryExecutionsFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listSqlQueryExecutions>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listSqlQueryExecutions(projectId, ws, pt, ps, body),
      'listSqlQueryExecutions',
      5000
    ),
    startSqlQueryExecution: (
      workspaceId: string,
      request: StartSqlQueryExecutionInput
    ) =>
      lakehouseApi.startSqlQueryExecution(
        currentProjectId,
        workspaceId,
        request
      ),
    getSqlQueryExecution: (workspaceId: string, sqlQueryExecutionId: string) =>
      lakehouseApi.getSqlQueryExecution(
        currentProjectId,
        workspaceId,
        sqlQueryExecutionId
      ),
    useGetSqlQueryExecution: (
      workspaceId: string | null,
      sqlQueryExecutionId: string | null
    ) =>
      httpClient.execute(
        lakehouseApi.getSqlQueryExecution.bind(lakehouseApi),
        workspaceId && sqlQueryExecutionId ? baseUrl : null,
        'getSqlQueryExecution',
        currentProjectId,
        workspaceId ?? '',
        sqlQueryExecutionId ?? ''
      ),
    getSqlQueryExecutionResult: (
      workspaceId: string,
      sqlQueryExecutionId: string,
      pageToken?: string,
      pageSize?: number
    ) =>
      lakehouseApi.getSqlQueryExecutionResult(
        currentProjectId,
        workspaceId,
        sqlQueryExecutionId,
        pageToken,
        pageSize
      ),

    // Spark Engine operations.
    // Non-paginated helper for selectors and panels — pageSize=200 (spec max).
    // Use `useListSparkEnginesPaginated` for the engines page.
    useListSparkEngines: (workspaceId: string | null) =>
      httpClient.execute(
        (projectId: string, ws: string) =>
          lakehouseApi.listSparkEngines(projectId, ws, undefined, 200),
        baseUrl,
        'listSparkEngines',
        currentProjectId,
        workspaceId ?? ''
      ),
    useListSparkEnginesPaginated: buildPaginatedListHook<
      SortSparkEngineBy,
      ListSparkEnginesFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listSparkEngines>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listSparkEngines(projectId, ws, pt, ps, body),
      'listSparkEnginesPaginated',
      5000
    ),
    listDistinctNamesFromSparkEngines: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listSparkEngines(currentProjectId, workspaceId, pt, 200),
        (r) => r.sparkEngines,
        (r) => r.nextToken,
        (e, set) => {
          if (e.name) {
            set.add(e.name);
          }
        },
        maxResources,
        maxPages
      ),
    listDistinctInstanceFamiliesFromSparkEngines: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listSparkEngines(currentProjectId, workspaceId, pt, 200),
        (r) => r.sparkEngines,
        (r) => r.nextToken,
        (e, set) => {
          if (e.instanceFamily) {
            set.add(e.instanceFamily);
          }
        },
        maxResources,
        maxPages
      ),
    createSparkEngine: (workspaceId: string, request: CreateSparkEngineInput) =>
      lakehouseApi.createSparkEngine(currentProjectId, workspaceId, request),
    getSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      lakehouseApi.getSparkEngine(currentProjectId, workspaceId, sparkEngineId),
    useGetSparkEngine: (
      workspaceId: string | null,
      sparkEngineId: string | null
    ) =>
      httpClient.execute(
        lakehouseApi.getSparkEngine.bind(lakehouseApi),
        workspaceId && sparkEngineId ? baseUrl : null,
        'getSparkEngine',
        currentProjectId,
        workspaceId ?? '',
        sparkEngineId ?? ''
      ),
    deleteSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      handle204(() =>
        lakehouseApi.deleteSparkEngine(
          currentProjectId,
          workspaceId,
          sparkEngineId
        )
      ),
    startSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      lakehouseApi.startSparkEngine(
        currentProjectId,
        workspaceId,
        sparkEngineId
      ),
    stopSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      lakehouseApi.stopSparkEngine(
        currentProjectId,
        workspaceId,
        sparkEngineId
      ),

    // Spark Job operations.
    // Non-paginated helper for the spark engine panel (lists jobs for a
    // single engine in a sub-section). Use `useListSparkJobsPaginated` for
    // the jobs page itself.
    useListSparkJobs: (workspaceId: string | null) =>
      httpClient.execute(
        (projectId: string, ws: string) =>
          lakehouseApi.listSparkJobs(projectId, ws, undefined, 200),
        baseUrl,
        'listSparkJobs',
        currentProjectId,
        workspaceId ?? ''
      ),
    useListSparkJobsPaginated: buildPaginatedListHook<
      SortSparkJobBy,
      ListSparkJobsFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listSparkJobs>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listSparkJobs(projectId, ws, pt, ps, body),
      'listSparkJobsPaginated',
      10000
    ),
    listDistinctNamesFromSparkJobs: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listSparkJobs(currentProjectId, workspaceId, pt, 200),
        (r) => r.sparkJobs,
        (r) => r.nextToken,
        (j, set) => {
          if (j.name) {
            set.add(j.name);
          }
        },
        maxResources,
        maxPages
      ),
    startSparkJob: (workspaceId: string, request: StartSparkJobInput) =>
      lakehouseApi.startSparkJob(currentProjectId, workspaceId, request),
    useGetSparkJob: (workspaceId: string | null, sparkJobId: string | null) =>
      httpClient.execute(
        lakehouseApi.getSparkJob.bind(lakehouseApi),
        workspaceId && sparkJobId ? baseUrl : null,
        'getSparkJob',
        currentProjectId,
        workspaceId ?? '',
        sparkJobId ?? ''
      ),

    // Maintenance Run operations.
    // Workspace-scoped list of Iceberg/Paimon table maintenance runs
    // (compaction, expire snapshots, remove orphan files, …).
    useListMaintenanceRunsPaginated: buildPaginatedListHook<
      SortMaintenanceRunBy,
      ListMaintenanceRunsFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listMaintenanceRuns>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listMaintenanceRuns(projectId, ws, pt, ps, body),
      'listMaintenanceRunsPaginated',
      10000
    ),
    useGetMaintenanceRun: (workspaceId: string | null, runId: string | null) =>
      httpClient.execute(
        lakehouseApi.getMaintenanceRun.bind(lakehouseApi),
        workspaceId && runId ? baseUrl : null,
        'getMaintenanceRun',
        currentProjectId,
        workspaceId ?? '',
        runId ?? ''
      ),
    listDistinctDatabaseNamesFromMaintenanceRuns: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listMaintenanceRuns(
            currentProjectId,
            workspaceId,
            pt,
            200
          ),
        (r) => r.maintenanceRuns,
        (r) => r.nextToken,
        (run, set) => {
          if (run.databaseName) {
            set.add(run.databaseName);
          }
        },
        maxResources,
        maxPages
      ),
    listDistinctTableNamesFromMaintenanceRuns: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listMaintenanceRuns(
            currentProjectId,
            workspaceId,
            pt,
            200
          ),
        (r) => r.maintenanceRuns,
        (r) => r.nextToken,
        (run, set) => {
          if (run.tableName) {
            set.add(run.tableName);
          }
        },
        maxResources,
        maxPages
      ),

    // Job Template operations.
    // Non-paginated helper for the panel / wizards that resolve all templates
    // at once — pageSize=200 (spec max). Use `useListJobTemplatesPaginated`
    // for the templates page.
    useListJobTemplates: (workspaceId: string | null) =>
      httpClient.execute(
        (projectId: string, ws: string) =>
          lakehouseApi.listJobTemplates(projectId, ws, undefined, 200),
        baseUrl,
        'listJobTemplates',
        currentProjectId,
        workspaceId ?? ''
      ),
    useListJobTemplatesPaginated: buildPaginatedListHook<
      SortJobTemplateBy,
      ListJobTemplatesFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listJobTemplates>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listJobTemplates(projectId, ws, pt, ps, body),
      'listJobTemplatesPaginated',
      30000
    ),
    listDistinctNamesFromJobTemplates: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listJobTemplates(currentProjectId, workspaceId, pt, 200),
        (r) => r.jobTemplates,
        (r) => r.nextToken,
        (t, set) => {
          if (t.name) {
            set.add(t.name);
          }
        },
        maxResources,
        maxPages
      ),
    createJobTemplate: (workspaceId: string, request: CreateJobTemplateInput) =>
      lakehouseApi.createJobTemplate(currentProjectId, workspaceId, request),
    updateJobTemplate: (
      workspaceId: string,
      jobTemplateId: string,
      request: UpdateJobTemplateInput
    ) =>
      lakehouseApi.updateJobTemplate(
        currentProjectId,
        workspaceId,
        jobTemplateId,
        request
      ),
    useGetJobTemplate: (
      workspaceId: string | null,
      jobTemplateId: string | null
    ) =>
      httpClient.execute(
        lakehouseApi.getJobTemplate.bind(lakehouseApi),
        workspaceId && jobTemplateId ? baseUrl : null,
        'getJobTemplate',
        currentProjectId,
        workspaceId ?? '',
        jobTemplateId ?? ''
      ),
    deleteJobTemplate: (workspaceId: string, jobTemplateId: string) =>
      handle204(() =>
        lakehouseApi.deleteJobTemplate(
          currentProjectId,
          workspaceId,
          jobTemplateId
        )
      ),

    // Saved Query operations. Cursor-token pagination — caller tracks tokens
    // by page index and passes the right one here. `keepPreviousData` retains
    // the prior key's data while a filter/sort/page change is in flight so the
    // surrounding UI (FilterStrip, popovers) doesn't unmount mid-interaction.
    useListSavedQueries: buildPaginatedListHook<
      SortSavedQueryBy,
      ListSavedQueriesFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listSavedQueries>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listSavedQueries(projectId, ws, pt, ps, body),
      'listSavedQueries',
      30000
    ),
    listDistinctTagsFromSavedQueries: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listSavedQueries(currentProjectId, workspaceId, pt, 200),
        (r) => r.savedQueries,
        (r) => r.nextToken,
        (q, set) => {
          for (const t of q.tags ?? []) {
            if (t) {
              set.add(t);
            }
          }
        },
        maxResources,
        maxPages
      ),
    listDistinctNamesFromSavedQueries: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listSavedQueries(currentProjectId, workspaceId, pt, 200),
        (r) => r.savedQueries,
        (r) => r.nextToken,
        (q, set) => {
          if (q.name) {
            set.add(q.name);
          }
        },
        maxResources,
        maxPages
      ),
    createSavedQuery: (workspaceId: string, request: CreateSavedQueryInput) =>
      lakehouseApi.createSavedQuery(currentProjectId, workspaceId, request),
    getSavedQuery: (workspaceId: string, savedQueryId: string) =>
      lakehouseApi.getSavedQuery(currentProjectId, workspaceId, savedQueryId),
    useGetSavedQuery: (
      workspaceId: string | null,
      savedQueryId: string | null
    ) =>
      httpClient.execute(
        lakehouseApi.getSavedQuery.bind(lakehouseApi),
        workspaceId && savedQueryId ? baseUrl : null,
        'getSavedQuery',
        currentProjectId,
        workspaceId ?? '',
        savedQueryId ?? ''
      ),
    updateSavedQuery: (
      workspaceId: string,
      savedQueryId: string,
      request: UpdateSavedQueryInput
    ) =>
      lakehouseApi.updateSavedQuery(
        currentProjectId,
        workspaceId,
        savedQueryId,
        request
      ),
    deleteSavedQuery: (workspaceId: string, savedQueryId: string) =>
      handle204(() =>
        lakehouseApi.deleteSavedQuery(
          currentProjectId,
          workspaceId,
          savedQueryId
        )
      ),

    // Ingestion Pipeline operations.
    // Non-paginated helper for the wizard/panel — pageSize=200 (spec max).
    // Use `useListIngestionPipelinesPaginated` for the pipelines page itself.
    useListIngestionPipelines: (workspaceId: string | null) =>
      httpClient.execute(
        (projectId: string, ws: string) =>
          lakehouseApi.listIngestionPipelines(projectId, ws, undefined, 200),
        baseUrl,
        'listIngestionPipelines',
        currentProjectId,
        workspaceId ?? ''
      ),
    useListIngestionPipelinesPaginated: buildPaginatedListHook<
      SortIngestionPipelineBy,
      ListIngestionPipelinesFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listIngestionPipelines>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listIngestionPipelines(projectId, ws, pt, ps, body),
      'listIngestionPipelinesPaginated',
      5000
    ),
    listDistinctNamesFromIngestionPipelines: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listIngestionPipelines(
            currentProjectId,
            workspaceId,
            pt,
            200
          ),
        (r) => r.ingestionPipelines,
        (r) => r.nextToken,
        (p, set) => {
          if (p.name) {
            set.add(p.name);
          }
        },
        maxResources,
        maxPages
      ),
    listDistinctCatalogIdsFromIngestionPipelines: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listIngestionPipelines(
            currentProjectId,
            workspaceId,
            pt,
            200
          ),
        (r) => r.ingestionPipelines,
        (r) => r.nextToken,
        (p, set) => {
          if (p.catalogId) {
            set.add(p.catalogId);
          }
        },
        maxResources,
        maxPages
      ),
    createIngestionPipeline: (
      workspaceId: string,
      request: CreateIngestionPipelineInput
    ) =>
      lakehouseApi.createIngestionPipeline(
        currentProjectId,
        workspaceId,
        request
      ),
    useGetIngestionPipeline: (
      workspaceId: string | null,
      ingestionPipelineId: string | null
    ) =>
      httpClient.execute(
        lakehouseApi.getIngestionPipeline.bind(lakehouseApi),
        workspaceId && ingestionPipelineId ? baseUrl : null,
        'getIngestionPipeline',
        currentProjectId,
        workspaceId ?? '',
        ingestionPipelineId ?? ''
      ),
    deleteIngestionPipeline: (
      workspaceId: string,
      ingestionPipelineId: string
    ) =>
      handle204(() =>
        lakehouseApi.deleteIngestionPipeline(
          currentProjectId,
          workspaceId,
          ingestionPipelineId
        )
      ),
    startIngestionPipeline: (
      workspaceId: string,
      ingestionPipelineId: string
    ) =>
      lakehouseApi.startIngestionPipeline(
        currentProjectId,
        workspaceId,
        ingestionPipelineId
      ),
    stopIngestionPipeline: (workspaceId: string, ingestionPipelineId: string) =>
      lakehouseApi.stopIngestionPipeline(
        currentProjectId,
        workspaceId,
        ingestionPipelineId
      ),
    // Metrics endpoints. Hand-rolled fetches against the lakehouse CP for
    // now — the matching SDK methods (``getIngestionPipelineMetrics`` /
    // ``getIngestionPipelineMetricsRange``) haven't been regenerated into
    // @repo/api-gw-sdk yet. Swap once they land.
    useIngestionPipelineMetrics: (
      workspaceId: string | null,
      ingestionPipelineId: string | null,
      refreshIntervalMs = 5_000
    ): {
      body: IngestionPipelineMetricsPayload | undefined;
      isLoading: boolean;
      error: unknown;
    } => {
      const url =
        workspaceId && ingestionPipelineId
          ? `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/ingestion-pipeline/${ingestionPipelineId}/metrics`
          : null;
      return httpClient.get(url, {
        refreshInterval: refreshIntervalMs,
        shouldRetryOnError: false,
      });
    },
    useIngestionPipelineMetricsRange: (
      workspaceId: string | null,
      ingestionPipelineId: string | null,
      params: {
        series: string[];
        startTs: number;
        endTs: number;
        stepSeconds: number;
      } | null,
      refreshIntervalMs = 30_000
    ): {
      body: IngestionPipelineMetricsRangePayload | undefined;
      isLoading: boolean;
      error: unknown;
    } => {
      let url: string | null = null;
      if (workspaceId && ingestionPipelineId && params) {
        const q = new URLSearchParams();
        for (const s of params.series) {
          q.append('series', s);
        }
        q.set('start_ts', String(params.startTs));
        q.set('end_ts', String(params.endTs));
        q.set('step_seconds', String(params.stepSeconds));
        url = `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/ingestion-pipeline/${ingestionPipelineId}/metrics/range?${q.toString()}`;
      }
      return httpClient.get(url, {
        refreshInterval: refreshIntervalMs,
        shouldRetryOnError: false,
      });
    },
    updateIngestionPipeline: (
      workspaceId: string,
      ingestionPipelineId: string,
      request: UpdateIngestionPipelineInput
    ) =>
      lakehouseApi.updateIngestionPipeline(
        currentProjectId,
        workspaceId,
        ingestionPipelineId,
        request
      ),

    // Credentials Profile operations.
    // Non-paginated helper for selectors and panels that need all profiles —
    // pageSize=200 (spec max). Use `useListCredentialsProfilesPaginated` for
    // the profiles page itself.
    useListCredentialsProfiles: (workspaceId: string | null) =>
      httpClient.execute(
        (projectId: string, ws: string) =>
          lakehouseApi.listCredentialsProfiles(projectId, ws, undefined, 200),
        baseUrl,
        'listCredentialsProfiles',
        currentProjectId,
        workspaceId ?? ''
      ),
    useListCredentialsProfilesPaginated: buildPaginatedListHook<
      SortCredentialsProfileBy,
      ListCredentialsProfilesFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listCredentialsProfiles>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listCredentialsProfiles(projectId, ws, pt, ps, body),
      'listCredentialsProfilesPaginated',
      30000
    ),
    listDistinctNamesFromCredentialsProfiles: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listCredentialsProfiles(
            currentProjectId,
            workspaceId,
            pt,
            200
          ),
        (r) => r.credentialsProfiles,
        (r) => r.nextToken,
        (p, set) => {
          if (p.name) {
            set.add(p.name);
          }
        },
        maxResources,
        maxPages
      ),
    useGetCredentialsProfile: (
      workspaceId: string | null,
      credentialsProfileId: string | null
    ) =>
      httpClient.execute(
        lakehouseApi.getCredentialsProfile.bind(lakehouseApi),
        workspaceId && credentialsProfileId ? baseUrl : null,
        'getCredentialsProfile',
        currentProjectId,
        workspaceId ?? '',
        credentialsProfileId ?? ''
      ),
    createCredentialsProfile: (
      workspaceId: string,
      request: CreateCredentialsProfileInput
    ) =>
      lakehouseApi.createCredentialsProfile(
        currentProjectId,
        workspaceId,
        request
      ),
    updateCredentialsProfile: (
      workspaceId: string,
      credentialsProfileId: string,
      request: UpdateCredentialsProfileInput
    ) =>
      lakehouseApi.updateCredentialsProfile(
        currentProjectId,
        workspaceId,
        credentialsProfileId,
        request
      ),
    deleteCredentialsProfile: (
      workspaceId: string,
      credentialsProfileId: string
    ) =>
      handle204(() =>
        lakehouseApi.deleteCredentialsProfile(
          currentProjectId,
          workspaceId,
          credentialsProfileId
        )
      ),

    // Data Catalog operations
    useListDataCatalogs: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listDataCatalogs.bind(lakehouseApi),
        baseUrl,
        'listDataCatalogs',
        currentProjectId,
        workspaceId ?? ''
      ),
    listDataCatalogs: (workspaceId: string) =>
      lakehouseApi.listDataCatalogs(currentProjectId, workspaceId),
    createDataCatalog: (workspaceId: string, request: CreateDataCatalogInput) =>
      lakehouseApi.createDataCatalog(currentProjectId, workspaceId, request),
    deleteDataCatalog: (workspaceId: string, catalogId: string) =>
      handle204(() =>
        lakehouseApi.deleteDataCatalog(currentProjectId, workspaceId, catalogId)
      ),
    accessDataCatalog: (
      workspaceId: string,
      catalogId: string,
      request: CatalogAccessRequest
    ) =>
      lakehouseApi.accessDataCatalog(
        currentProjectId,
        workspaceId,
        catalogId,
        request
      ),

    // Ingestion pipeline wizard probes — read-only, side-effect-free.
    // The wizard calls these per-step to live-validate config against the actual
    // Kafka cluster, Schema Registry, catalogs, and credentials profile.
    probeCredentialsProfileAssume: (
      workspaceId: string,
      credentialsProfileId: string
    ) =>
      lakehouseApi.probeCredentialsProfileAssume(
        currentProjectId,
        workspaceId,
        credentialsProfileId
      ),
    probeCredentialsProfileDataLakeWrite: (
      workspaceId: string,
      credentialsProfileId: string
    ) =>
      lakehouseApi.probeCredentialsProfileDataLakeWrite(
        currentProjectId,
        workspaceId,
        credentialsProfileId
      ),
    probeCredentialsProfileFlinkStateWrite: (
      workspaceId: string,
      credentialsProfileId: string
    ) =>
      lakehouseApi.probeCredentialsProfileFlinkStateWrite(
        currentProjectId,
        workspaceId,
        credentialsProfileId
      ),
    probeKafkaSecret: (workspaceId: string, request: KafkaSecretProbeRequest) =>
      lakehouseApi.probeKafkaSecret(currentProjectId, workspaceId, request),
    probeKafkaConnect: (
      workspaceId: string,
      request: KafkaConnectProbeRequest
    ) => lakehouseApi.probeKafkaConnect(currentProjectId, workspaceId, request),
    probeKafkaTopics: (workspaceId: string, request: KafkaTopicsProbeRequest) =>
      lakehouseApi.probeKafkaTopics(currentProjectId, workspaceId, request),
    probeKafkaSample: (workspaceId: string, request: KafkaSampleProbeRequest) =>
      lakehouseApi.probeKafkaSample(currentProjectId, workspaceId, request),
    probeKafkaInferSchema: (
      workspaceId: string,
      request: KafkaInferSchemaRequest
    ) =>
      lakehouseApi.probeKafkaInferSchema(
        currentProjectId,
        workspaceId,
        request
      ),
    probeSchemaRegistryConnect: (
      workspaceId: string,
      request: SchemaRegistryConnectRequest
    ) =>
      lakehouseApi.probeSchemaRegistryConnect(
        currentProjectId,
        workspaceId,
        request
      ),
    probeSchemaRegistrySchemaById: (
      workspaceId: string,
      request: SchemaRegistrySchemaByIdRequest
    ) =>
      lakehouseApi.probeSchemaRegistrySchemaById(
        currentProjectId,
        workspaceId,
        request
      ),
    probeSchemaRegistryResolveSubject: (
      workspaceId: string,
      request: SchemaRegistryResolveSubjectRequest
    ) =>
      lakehouseApi.probeSchemaRegistryResolveSubject(
        currentProjectId,
        workspaceId,
        request
      ),
    probeCatalogListDatabases: (
      workspaceId: string,
      request: CatalogListDatabasesRequest
    ) =>
      lakehouseApi.probeCatalogListDatabases(
        currentProjectId,
        workspaceId,
        request
      ),
    probeCatalogCheckName: (
      workspaceId: string,
      request: CatalogCheckNameRequest
    ) =>
      lakehouseApi.probeCatalogCheckName(
        currentProjectId,
        workspaceId,
        request
      ),
    probeS3ListPrefixes: (
      workspaceId: string,
      request: S3ListPrefixesRequest
    ) =>
      lakehouseApi.probeS3ListPrefixes(currentProjectId, workspaceId, request),
    probeGcsListPrefixes: (
      workspaceId: string,
      request: GcsListPrefixesRequest
    ) =>
      lakehouseApi.probeGcsListPrefixes(currentProjectId, workspaceId, request),
    probeS3DetectPartitions: (
      workspaceId: string,
      request: S3DetectPartitionsRequest
    ) =>
      lakehouseApi.probeS3DetectPartitions(
        currentProjectId,
        workspaceId,
        request
      ),
    probeBucketNotification: (
      workspaceId: string,
      request: BucketNotificationProbeRequest
    ) =>
      lakehouseApi.probeBucketNotification(
        currentProjectId,
        workspaceId,
        request
      ),
    probeBucketFile: (workspaceId: string, request: BucketFileProbeRequest) =>
      lakehouseApi.probeBucketFile(currentProjectId, workspaceId, request),

    // Notebook operations.
    // Non-paginated helper for the notebook panel and other consumers that
    // need to resolve all notebooks at once — pageSize=200 (spec max). Use
    // `useListNotebooksPaginated` for the notebooks page.
    useListNotebooks: (workspaceId: string | null) =>
      httpClient.execute(
        (projectId: string, ws: string) =>
          lakehouseApi.listNotebooks(projectId, ws, undefined, 200),
        baseUrl,
        'listNotebooks',
        currentProjectId,
        workspaceId ?? ''
      ),
    useListNotebooksPaginated: buildPaginatedListHook<
      SortNotebookBy,
      ListNotebooksFilterConditions,
      Awaited<ReturnType<typeof lakehouseApi.listNotebooks>>
    >(
      (projectId, ws, pt, ps, body) =>
        lakehouseApi.listNotebooks(projectId, ws, pt, ps, body),
      'listNotebooksPaginated',
      30000
    ),
    listDistinctNamesFromNotebooks: (
      workspaceId: string,
      maxResources = 1000,
      maxPages = 50
    ): Promise<string[]> =>
      sampleDistinct(
        (pt) =>
          lakehouseApi.listNotebooks(currentProjectId, workspaceId, pt, 200),
        (r) => r.notebooks,
        (r) => r.nextToken,
        (n, set) => {
          if (n.name) {
            set.add(n.name);
          }
        },
        maxResources,
        maxPages
      ),
    createNotebook: (workspaceId: string, request: CreateNotebookInput) =>
      lakehouseApi.createNotebook(currentProjectId, workspaceId, request),
    getNotebook: (workspaceId: string, notebookId: string) =>
      lakehouseApi.getNotebook(currentProjectId, workspaceId, notebookId),
    useGetNotebook: (workspaceId: string | null, notebookId: string | null) =>
      httpClient.execute(
        lakehouseApi.getNotebook.bind(lakehouseApi),
        workspaceId && notebookId ? baseUrl : null,
        'getNotebook',
        currentProjectId,
        workspaceId ?? '',
        notebookId ?? ''
      ),
    deleteNotebook: (workspaceId: string, notebookId: string) =>
      handle204(() =>
        lakehouseApi.deleteNotebook(currentProjectId, workspaceId, notebookId)
      ),
    getNotebookContent: (workspaceId: string, notebookId: string) =>
      lakehouseApi.getNotebookContent(
        currentProjectId,
        workspaceId,
        notebookId
      ),
    saveNotebookContent: (
      workspaceId: string,
      notebookId: string,
      content: object
    ) =>
      handle204(() =>
        lakehouseApi.saveNotebookContent(
          currentProjectId,
          workspaceId,
          notebookId,
          {
            content,
          }
        )
      ),

    // Notebook Session operations
    startNotebookSession: (
      workspaceId: string,
      notebookId: string,
      request: StartNotebookSessionInput
    ) =>
      lakehouseApi.startNotebookSession(
        currentProjectId,
        workspaceId,
        notebookId,
        request
      ),
    getNotebookSession: (workspaceId: string, notebookId: string) =>
      lakehouseApi.getNotebookSession(
        currentProjectId,
        workspaceId,
        notebookId
      ),
    stopNotebookSession: (workspaceId: string, notebookId: string) =>
      lakehouseApi.stopNotebookSession(
        currentProjectId,
        workspaceId,
        notebookId
      ),

    // Notebook Execution operations
    executeNotebookCode: (
      workspaceId: string,
      notebookId: string,
      request: ExecuteNotebookCodeInput
    ) =>
      lakehouseApi.executeNotebookCode(
        currentProjectId,
        workspaceId,
        notebookId,
        request
      ),
    getNotebookExecutionResult: (
      workspaceId: string,
      notebookId: string,
      executionId: string
    ) =>
      lakehouseApi.getNotebookExecutionResult(
        currentProjectId,
        workspaceId,
        notebookId,
        executionId
      ),

    // Dashboard operations
    useListDashboards: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listDashboards.bind(lakehouseApi),
        baseUrl,
        'listDashboards',
        currentProjectId,
        workspaceId ?? ''
      ),
    getDashboard: (workspaceId: string, dashboardId: string) =>
      lakehouseApi.getDashboard(currentProjectId, workspaceId, dashboardId),
    createDashboard: (workspaceId: string, input: CreateDashboardInput) =>
      lakehouseApi.createDashboard(currentProjectId, workspaceId, input),
    updateDashboard: (
      workspaceId: string,
      dashboardId: string,
      input: UpdateDashboardInput
    ) =>
      lakehouseApi.updateDashboard(
        currentProjectId,
        workspaceId,
        dashboardId,
        input
      ),
    deleteDashboard: (workspaceId: string, dashboardId: string) =>
      handle204(() =>
        lakehouseApi.deleteDashboard(currentProjectId, workspaceId, dashboardId)
      ),

    // Dashboard Graph operations
    createGraph: (
      workspaceId: string,
      dashboardId: string,
      input: CreateGraphInput
    ) =>
      lakehouseApi.createGraph(
        currentProjectId,
        workspaceId,
        dashboardId,
        input
      ),
    getGraph: (workspaceId: string, dashboardId: string, graphId: string) =>
      lakehouseApi.getGraph(
        currentProjectId,
        workspaceId,
        dashboardId,
        graphId
      ),
    updateGraph: (
      workspaceId: string,
      dashboardId: string,
      graphId: string,
      input: UpdateGraphInput
    ) =>
      lakehouseApi.updateGraph(
        currentProjectId,
        workspaceId,
        dashboardId,
        graphId,
        input
      ),
    deleteGraph: (workspaceId: string, dashboardId: string, graphId: string) =>
      handle204(() =>
        lakehouseApi.deleteGraph(
          currentProjectId,
          workspaceId,
          dashboardId,
          graphId
        )
      ),

    getStorageAccessConfiguration: (workspaceId: string) =>
      lakehouseApi.getWorkspaceStorageAccessConfiguration(
        currentProjectId,
        workspaceId
      ),
    useGetStorageAccessConfiguration: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.getWorkspaceStorageAccessConfiguration.bind(lakehouseApi),
        workspaceId ? baseUrl : null,
        'getWorkspaceStorageAccessConfiguration',
        currentProjectId,
        workspaceId ?? ''
      ),
    updateStorageAccessConfiguration: (
      workspaceId: string,
      input: UpdateWorkspaceStorageAccessConfigurationInput
    ) =>
      lakehouseApi
        .updateWorkspaceStorageAccessConfiguration(
          currentProjectId,
          workspaceId,
          input
        )
        .then((result) => {
          void mutate([
            baseUrl,
            'getWorkspaceStorageAccessConfiguration',
            currentProjectId,
            workspaceId,
          ]);
          return result;
        }),
    getNetworkingSettings: async (workspaceId: string) => {
      // AWS-only today; flatten the cloud-tagged response so the tab stays cloud-neutral.
      const result = await lakehouseApi.getWorkspaceNetworkingSettings(
        currentProjectId,
        workspaceId
      );
      return {
        vpcPeering: result.aws?.vpcPeering ?? null,
        privateLinks: result.aws?.privateLinks ?? [],
      };
    },
    createVpcPeering: (workspaceId: string, input: CreateAwsVpcPeeringInput) =>
      lakehouseApi.createWorkspaceAwsVpcPeering(
        currentProjectId,
        workspaceId,
        input
      ),
    deleteVpcPeering: (workspaceId: string) =>
      handle204(() =>
        lakehouseApi.deleteWorkspaceAwsVpcPeering(currentProjectId, workspaceId)
      ),
    createPrivateLink: (
      workspaceId: string,
      input: CreateAwsPrivateLinkInput
    ) =>
      lakehouseApi.createWorkspaceAwsPrivateLink(
        currentProjectId,
        workspaceId,
        input
      ),
    updatePrivateLink: (
      workspaceId: string,
      privateLinkId: string,
      input: UpdateAwsPrivateLinkInput
    ) =>
      lakehouseApi.updateWorkspaceAwsPrivateLink(
        currentProjectId,
        workspaceId,
        privateLinkId,
        input
      ),
    deletePrivateLink: (workspaceId: string, privateLinkId: string) =>
      handle204(() =>
        lakehouseApi.deleteWorkspaceAwsPrivateLink(
          currentProjectId,
          workspaceId,
          privateLinkId
        )
      ),

    // Observability settings
    getLoggingSettings: (workspaceId: string) =>
      lakehouseApi.getWorkspaceLoggingSettings(currentProjectId, workspaceId),
    updateLoggingSettings: (
      workspaceId: string,
      input: UpdateWorkspaceLoggingSettingsInput
    ) =>
      lakehouseApi.updateWorkspaceLoggingSettings(
        currentProjectId,
        workspaceId,
        input
      ),
    getMetricsSettings: (workspaceId: string) =>
      lakehouseApi.getWorkspaceMetricsSettings(currentProjectId, workspaceId),
    updateMetricsSettings: (
      workspaceId: string,
      input: UpdateWorkspaceMetricsSettingsInput
    ) =>
      lakehouseApi.updateWorkspaceMetricsSettings(
        currentProjectId,
        workspaceId,
        input
      ),
    getMaintenanceSettings: (workspaceId: string) =>
      lakehouseApi.getWorkspaceMaintenanceSettings(
        currentProjectId,
        workspaceId
      ),
    updateMaintenanceSettings: (
      workspaceId: string,
      input: UpdateWorkspaceMaintenanceSettingsRequest
    ) =>
      lakehouseApi.updateWorkspaceMaintenanceSettings(
        currentProjectId,
        workspaceId,
        input
      ),

    // Table Maintenance operations
    getTableMaintenanceConfig: (
      workspaceId: string,
      catalogId: string,
      databaseName: string,
      tableName: string
    ) =>
      lakehouseApi.getTableMaintenanceConfig(
        currentProjectId,
        workspaceId,
        catalogId,
        databaseName,
        tableName
      ),
    updateTableMaintenanceConfig: (
      workspaceId: string,
      catalogId: string,
      databaseName: string,
      tableName: string,
      input: UpdateTableMaintenanceConfigInput
    ) =>
      lakehouseApi.updateTableMaintenanceConfig(
        currentProjectId,
        workspaceId,
        catalogId,
        databaseName,
        tableName,
        input
      ),

    // Zero-ETL Policy operations
    useListZeroEtlPolicies: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listZeroEtlPolicies.bind(lakehouseApi),
        baseUrl,
        'listZeroEtlPolicies',
        currentProjectId,
        workspaceId ?? ''
      ),
    createZeroEtlPolicy: (
      workspaceId: string,
      request: CreateZeroEtlPolicyInput
    ) =>
      lakehouseApi.createZeroEtlPolicy(currentProjectId, workspaceId, request),
    updateZeroEtlPolicy: (
      workspaceId: string,
      zeroEtlPolicyId: string,
      request: UpdateZeroEtlPolicyInput
    ) =>
      lakehouseApi.updateZeroEtlPolicy(
        currentProjectId,
        workspaceId,
        zeroEtlPolicyId,
        request
      ),
    deleteZeroEtlPolicy: (workspaceId: string, zeroEtlPolicyId: string) =>
      handle204(() =>
        lakehouseApi.deleteZeroEtlPolicy(
          currentProjectId,
          workspaceId,
          zeroEtlPolicyId
        )
      ),
  };
};
